/* eslint-disable no-await-in-loop */
import { inject } from 'aurelia-framework';
import { customElement } from 'aurelia-templating';
import { GridStackOptions } from 'gridstack';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IWidget, TextWidget } from '@dynamik/common';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { IParameterisedWidget } from 'insight/src/interfaces/i-parameterised-widget';
import { printReportPdf } from '../../views/edit-reports/report-html-snapshot';

@customElement('report-page')
@inject(Element)
export class ReportPageElement {
  constructor(public element: HTMLElement) { }

  @bindable.none
  widgets: IWidget[];

  @bindable.number
  width: number = 29.7;

  @bindable.number
  height: number = 21;

  @bindable
  title: string;

  @bindable.number
  pageNumber: number;

  @bindable.number
  subPageNumber: number;

  @bindable.booleanAttr
  readonly: boolean;

  gridStackOptions: Partial<GridStackOptions>;

  printing: boolean;

  bind() {
    this.element.style.width = `${this.width}cm`;
    this.element.style.height = `${this.height}cm`;
    this.gridStackOptions = {
      alwaysShowResizeHandle: false,
      maxRow: 42,
      column: 60,
      margin: 0,
      disableDrag: this.readonly,
      disableResize: this.readonly,
      draggable: {
        handle: '.report-page__widget-drag-area'
      },
      resizable: {
        autoHide: true
      }
    };
  }

  async beginPrint() {
    this.printing = true;
    await this.setSubPageNumber();
    this.widgets.forEach(x => x.compose.currentViewModel.printed = true);
  }

  async setSubPageNumber() {
    const pagedWidget = this.getPagedWidget();
    if (pagedWidget && this.subPageNumber) {
      await pagedWidget.setPage(this.subPageNumber);
    }
  }

  async endPrint() {
    this.widgets.forEach(x => x.compose.currentViewModel.printed = false);
    this.printing = false;
  }

  /** Prints the page in the pdf-service, one PDF page per page of its paginating widget: each
   *  page is copied in its printed state into the same pages structure the reports view prints. */
  async print() {
    // the first paginating widget will be controlling pages
    const pagedWidget = this.getPagedWidget();
    const pageCount = this.getPageCount();
    const pagesContainer = document.createElement('div');
    pagesContainer.className = 'pdf-reports__pages-container';
    for (let i = 0; i < pageCount; i++) {
      this.pageNumber = i + 1;
      await pagedWidget?.setPage(i);
      await this.beginPrint();
      const pageContainer = document.createElement('div');
      pageContainer.className = 'pdf-reports__page-container';
      pageContainer.append(this.element.cloneNode(true));
      pagesContainer.append(pageContainer);
      await this.endPrint();
    }
    await printReportPdf(pagesContainer);
  }

  handleGridChange() {
    this.element.dispatchEvent(new CustomEvent('change', { bubbles: true }));
  }

  removeWidget(w: IWidget, index: number) {
    this.handleGridClick();
    this.widgets.splice(index, 1);
    this.handleGridChange();
  }

  handleGridClick() {
    this.widgets.forEach(x => x.selected = false);
    this.element.dispatchEvent(new CustomEvent('select-widget', { bubbles: true, detail: undefined }));
  }

  handleGridItemClick(w: IWidget) {
    this.handleGridClick();
    w.selected = true;
    this.element.dispatchEvent(new CustomEvent('select-widget', { bubbles: true, detail: w }));
  }

  getPagedWidget() {
    return this.widgets.find(x => x.compose?.currentViewModel.getPageCount?.() > 1)?.compose?.currentViewModel;
  }

  getPageCount() {
    return this.getPagedWidget()?.getPageCount?.() ?? 1;
  }

  getParameterisedPropertiesMetadata(): IParameterisedWidget[] {
    return this.widgets.map(x => ({
      widget: x,
      propertiesMetadata: x.compose?.currentViewModel.getParameterisedPropertiesMetadata?.() ?? []
    })).filter(x => x.propertiesMetadata.length);
  }
}
